'use client';

import crypto from 'crypto';
import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { CITATION_LIST_ID } from '../../../CitationList/constants';

export interface ICitation {
  /**
   * The name of the publication
   */
  publication: string;
  /**
   * The title of the article being referenced
   */
  title: string;
  /**
   * The URL to the article
   */
  url?: string;
  /**
   * The date the article was accessed, in seconds since the epoch (e.g. 1529726635146)
   */
  accessDate?: number;
  /**
   * The formatted date the article was accessed
   */
  accessDateFormatted?: string;
  /**
   * True if the citation is cited in the body of the article
   */
  showInline?: boolean;
  /**
   * True if the citation is selected. This is used to highlight the citation in the CitationList.
   */
  selected?: boolean;
  /**
   * A unique identifier for the citation
   */
  id: string;
  /**
   * The citation number for sorting and display
   */
  citationNumber?: number;
}

interface ICitationContext {
  citations: ICitation[];
  registerCitation: (citation: ICitation) => ICitation;
  unregisterCitation: (citation: ICitation) => void;
  citationListExpanded: boolean;
  setCitationListExpanded: (expanded: boolean) => void;
  shouldScrollToCitations: boolean;
  setShouldScrollToCitations: (shouldScroll: boolean) => void;
  scrollToCitationList: () => void;
}

const compareCitations = (a: ICitation, b: ICitation) => {
  return a.id === b.id;
};

const hashCitation = (citation: ICitation) => {
  const valueToHash = JSON.stringify(citation).toLowerCase().trim();
  const hash = crypto.createHash('sha256').update(valueToHash).digest('hex');
  return hash;
};

const CitationContext = createContext<ICitationContext | undefined>(undefined);

export const CitationContextProvider = ({ children }: { children: React.ReactNode }) => {
  const citationsRef = useRef<ICitation[]>([]);
  const [citationListExpanded, setCitationListExpanded] = useState(false);
  const [shouldScrollToCitations, setShouldScrollToCitations] = useState(false);

  const registerCitation = useCallback((citation: ICitation): ICitation => {
    const id = hashCitation(citation);
    const newCitation = {
      ...citation,
      id,
      citationNumber: citation.showInline ? citationsRef.current.length + 1 : undefined,
      accessDateFormatted: citation.accessDate
        ? `Accessed ${new Date(citation.accessDate).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}.`
        : undefined,
    };

    const existingCitation = citationsRef.current.find((c: ICitation) =>
      compareCitations(c, newCitation),
    );
    if (existingCitation) return existingCitation;

    citationsRef.current.push(newCitation);
    return newCitation;
  }, []);

  const unregisterCitation = useCallback((citation: ICitation) => {
    const id = hashCitation(citation);
    citationsRef.current = citationsRef.current.filter(
      (c: ICitation) => !compareCitations(c, { ...citation, id }),
    );
  }, []);

  const scrollToCitationList = useCallback(() => {
    window.history.pushState(null, '', `#${CITATION_LIST_ID}`);

    if (citationListExpanded) {
      document.getElementById(CITATION_LIST_ID)?.scrollIntoView({
        behavior: 'instant',
        block: 'start',
      });
    } else {
      setCitationListExpanded(true);
      setShouldScrollToCitations(true);
    }
  }, [citationListExpanded, setCitationListExpanded, setShouldScrollToCitations]);

  const contextValue: ICitationContext = {
    citations: citationsRef.current,
    citationListExpanded,
    setCitationListExpanded,
    registerCitation,
    unregisterCitation,
    shouldScrollToCitations,
    setShouldScrollToCitations,
    scrollToCitationList,
  };

  return <CitationContext.Provider value={contextValue}>{children}</CitationContext.Provider>;
};

export const useCitationContext = () => {
  const context = useContext(CitationContext);
  if (!context) {
    throw new Error('useCitationContext must be used within a CitationContextProvider');
  }
  return context;
};
